import type { CdtStudioTranslations } from "../en/CdtStudio";
import type { TranslationShape } from "../../store/types";

export const cdtStudioEs: TranslationShape<CdtStudioTranslations> = {
  pageTitle: "CDT Studio",
  loading: "Cargando planograma base...",
  loadFailed: "No se pudo cargar el planograma base.",
  inProgress: "El planograma base todavía se está generando. Inténtalo de nuevo en un momento.",
  itemsSummary: "{{skus}} · {{bays}}",
  itemsSkus_one: "1 SKU",
  itemsSkus_other: "{{count}} SKUs",
  itemsBays_one: "1 módulo",
  itemsBays_other: "{{count}} módulos",
  placementRule: {
    chip: "Regla de ubicación",
    where_one: "Observado en el estante {{shelves}}",
    where_other: "Observado en los estantes {{shelves}}",
    onBays_one: "módulo {{bays}}",
    onBays_other: "módulos {{bays}}",
    range: "{{from}} a {{to}}",
    title_one: "¿Mantener {{variant}} en el estante {{shelves}}?",
    title_other: "¿Mantener {{variant}} en los estantes {{shelves}}?",
    detail:
      "La agrupación seleccionada puede colocar algunos de estos productos en otros estantes al " +
      "regenerar el diseño. Guardar esta regla los mantiene en los estantes indicados, formando " +
      "un bloque limpio siempre que el mueble lo permita.",
    detailTreeHolds:
      "Guardar esta regla mantiene estos productos en los estantes indicados al generar los " +
      "planogramas, formando un bloque limpio siempre que el mueble lo permita.",
    cost_one:
      "Abarca 1 ubicación de estante. El ancho y el reparto de frentes siguen siendo flexibles, " +
      "sujetos a otras restricciones. Detalle completo en el recibo de finalización.",
    cost_other:
      "Abarca {{cells}} ubicaciones de estante. El ancho y el reparto de frentes siguen siendo " +
      "flexibles, sujetos a otras restricciones. Detalle completo en el recibo de finalización.",
    promotionNote:
      'Para aplicar esta regla por separado, "{{variant}}" pasa a ser su propio grupo de productos dentro de {{parent}}.',
    keep: "Usar esta regla",
    decline: "Dejar la ubicación flexible",
    // No "Deshacer": reabre la pregunta de ESTA regla. Deshacer es la barra.
    changeAnswer: "Cambiar respuesta",
    keptChip: "Seleccionada — se guarda al terminar",
    declinedChip: "Ubicación flexible",
    keptSummary_one: "1 regla de ubicación seleccionada — se guarda al terminar",
    keptSummary_other: "{{count}} reglas de ubicación seleccionadas — se guardan al terminar",
    bandsTitle_one: "¿Mantener la franja de estantes que muestra el planograma base?",
    bandsTitle_other: "¿Mantener las {{count}} franjas de estantes que muestra el planograma base?",
    bandsIntro_one:
      "Studio encontró 1 franja de estantes en este mueble, {{sections}}: dentro de cada bloque vertical de marca, una forma de producto se queda en los mismos estantes (el estante 1 es el de arriba).",
    bandsIntro_other:
      "Studio encontró {{count}} franjas de estantes en este mueble, {{sections}}: dentro de cada bloque vertical de marca, una forma de producto se queda en los mismos estantes (el estante 1 es el de arriba).",
    bandsIntroNoSections_one:
      "Studio encontró 1 franja de estantes en este mueble: dentro de cada bloque vertical de marca, una forma de producto se queda en los mismos estantes (el estante 1 es el de arriba).",
    bandsIntroNoSections_other:
      "Studio encontró {{count}} franjas de estantes en este mueble: dentro de cada bloque vertical de marca, una forma de producto se queda en los mismos estantes (el estante 1 es el de arriba).",
    bandsIntroWholeBlocks_one:
      "Studio encontró 1 franja de estantes en este mueble: es un bloque que se queda en los mismos estantes (el estante 1 es el de arriba).",
    bandsIntroWholeBlocks_other:
      "Studio encontró {{count}} franjas de estantes en este mueble: cada una es un bloque que se queda en los mismos estantes (el estante 1 es el de arriba).",
    wholeBlock: "bloque entero",
    bandsInSection: "{{count}} en {{section}}",
    bandsExplain:
      "Una franja marcada se mantiene en cada planograma de tienda, incluso donde la tienda tiene menos estantes (las franjas conservan su orden de arriba abajo). Una franja sin marcar puede ir a cualquier estante cuando la tienda va justa de espacio.",
    bandsColumnBand: "Franja",
    bandsColumnStays: "Se queda en",
    bandsColumnAcross: "A lo largo de",
    staysOn_one: "estante {{shelves}}",
    staysOn_other: "estantes {{shelves}}",
    bandsKeep_one: "Mantener esta 1",
    bandsKeep_other: "Mantener estas {{count}}",
    bandsKeepNone: "No mantener ninguna",
    bandsKeepNote:
      "(el número sigue a tus marcas; las franjas cuentan como respondidas cuando lo pulsas)",
    showOnWall: "Muéstramelo en el mueble",
    bandsKept_one: "1 franja de estantes mantenida",
    bandsKept_other: "{{count}} franjas de estantes mantenidas",
    bandsFlexible_one: "1 queda flexible",
    bandsFlexible_other: "{{count}} quedan flexibles",
    bandsChange: "Cambiar",
    bandsDeclined_one: "1 franja puede ir a cualquier estante cuando la tienda va justa de espacio",
    bandsDeclined_other:
      "{{count}} franjas pueden ir a cualquier estante cuando la tienda va justa de espacio",
  },
  setup: {
    title: "Configuración de CDT Studio",
    intro:
      "Define el perfil de nombres y las reglas de inferencia que usa CDT Studio. Los atributos de producto vienen de la API de categoría.",
    summary: "{{attributes}} atributos",
    summaryPlural: "{{attributes}} atributos",
    sections: {
      basic: "Perfil y columnas de origen",
      attributes: "Mapeo y orden de atributos",
      semantic: "Roles semánticos e inferencia",
      normalization: "Normalización de cantidades",
      hierarchy: "Movimientos de jerarquía",
      vertical: "Bloqueo vertical",
      aesthetics: "Estética",
      advanced: "Editor avanzado del perfil",
    },
    actions: {
      close: "Cerrar",
      reset: "Restablecer",
      save: "Guardar configuración",
      addAttribute: "Añadir atributo",
      addTier: "Añadir nivel",
      remove: "Eliminar",
      moveUp: "Subir",
      moveDown: "Bajar",
    },
    fields: {
      merchant: "Comercio",
      category: "Categoría",
      profileVersion: "Versión del perfil",
      itemIdentifier: "Identificador de artículo del POG",
      skuColumn: "Columna SKU",
      nameColumn: "Columna de nombre",
      attributeKey: "Clave",
      attributeLabel: "Etiqueta",
      sourceColumn: "Columna de origen",
      selectColumn: "Seleccionar columna CSV",
      pogField: "Campo del POG",
      notMapped: "Sin asignar — el usuario completa",
      missingValues: "Valores ausentes",
      blockIdentity: "Orden de identidad del bloque",
      verticalRoles: "Orden de roles verticales",
      brandRole: "Rol de marca",
      tierRole: "Rol de nivel",
      quantityRole: "Rol de cantidad",
      terminalVariantRole: "Rol de variante terminal",
      useFirstRegex: "Usar regex de primer valor",
      firstRegex: "Regex de primer valor",
      useLastRegex: "Usar regex de último valor",
      lastRegex: "Regex de último valor",
      normalizationEnabled: "Activar normalización de cantidades",
      inferFromName: "Inferir la cantidad del nombre del producto",
      zeroIsMissing: "Tratar cero como valor ausente",
      liquidOuncesAreFluid: "Tratar onzas líquidas como onzas fluidas",
      unitPreference: "Orden de preferencia de unidades",
      liquidNameTerms: "Términos líquidos en el nombre",
      minimumFamilyEvidence: "Evidencia mínima de familia",
      reconciliationMinimum: "Ratio mínimo de conciliación",
      reconciliationMaximum: "Ratio máximo de conciliación",
      minFacingsPerShelf: "Frentes mínimos por estante",
      roundTripFloorShare: "Proporción mínima de ida y vuelta",
      fillFloorShare: "Proporción mínima de ocupación",
      fillDropTolerancePts: "Tolerancia de caída de ocupación (puntos)",
      seamPass: "Pasada de alineación",
      blockShare: "Proporción de estantes del bloque",
      fragmentJoinPct: "Unión de fragmentos (% del estante)",
      minFillRatio: "Ratio mínimo de ocupación",
      preserveRange: "Conservar productos incluidos",
      onlyIfBetter: "Aplicar solo si mejora la puntuación",
      brandAttribute: "Atributo de marca",
      tierAttribute: "Atributo de nivel",
      sizeAttribute: "Atributo de cantidad",
      flavourAttribute: "Atributo de variante terminal",
      verticalAttributes: "Atributos verticales",
      tierLabel: "Etiqueta del nivel",
      tierValues: "Valores del nivel",
      lastInBlock: "Valores colocados al final del bloque",
      bayOwners: "Propietarios de módulo completo",
      bulkPounds: "Umbral de volumen (lb)",
      lowerShelfShare: "Proporción de estantes inferiores",
      jitterPacks: "Tolerancia del borde (paquetes)",
      raggedZeroPacks: "Irregularidad de puntuación cero (paquetes)",
      flagsPerHundredZero: "Alertas de puntuación cero por 100 SKUs",
      blockingPenalty: "Penalización de gravedad bloqueante",
      toCheckPenalty: "Penalización de gravedad por comprobar",
      toReviewPenalty: "Penalización de gravedad por revisar",
      blockingWeight: "Peso del bloqueo",
      cdtWeight: "Peso del CDT",
      fullnessWeight: "Peso de la ocupación",
      ergonomicsWeight: "Peso de la ergonomía",
      rulesKeptWeight: "Peso de las reglas de estante",
      rangeWeight: "Peso del surtido conservado",
      shapeSplit: "Reparto de forma de bloque",
      edgesSplit: "Reparto de bordes rectos",
      ladderSplit: "Reparto de escalera de niveles",
      detectorSplit: "Reparto del detector CDT",
      orderingSplit: "Reparto del orden CDT",
      fullProfileJson: "JSON completo del perfil",
    },
    tooltips: {
      merchant:
        "Solo metadatos. Nombra el comercio propietario del perfil guardado de la categoría.",
      category: "Solo metadatos. Nombra la configuración de categoría; no ordena el CDT.",
      profileVersion: "Metadato que identifica esta revisión; no cambia la inferencia.",
      itemIdentifier:
        "Selecciona el campo del POG que se enlaza con el maestro. Una elección incorrecta deja productos ausentes o mal asociados antes de inferir.",
      skuColumn: "Columna CSV con el identificador usado para enlazar filas y productos.",
      nameColumn:
        "Columna CSV del nombre. Se muestra y puede aportar evidencia de cantidad si la inferencia por nombre está activa.",
      attributeKey:
        "Nombre estable usado por el motor en inferencia, roles, reglas y superposiciones.",
      attributeLabel: "Etiqueta visible del nivel jerárquico. No cambia la inferencia.",
      sourceColumn: "Columna CSV cuyos valores alimentan este atributo de inferencia.",
      pogField:
        "Campo opcional del POG para precargar filas faltantes del maestro. No cambia filas existentes.",
      missingValues:
        "Estas grafías, sin distinguir mayúsculas, se convierten en Unspecified y no crean ramas significativas.",
      blockIdentity:
        "Solo resolutor. Atributos de mayor a menor para auditar divisiones y huérfanos; no reordena la inferencia inicial.",
      verticalRoles:
        "Puntuación y reparación. Atributos ordenados que definen un bloque vertical; no eligen el árbol inicial.",
      brandRole:
        "Puntuación y diagnóstico. Atributo usado para agrupar marcas y detectar eliminaciones.",
      tierRole:
        "Puntuación y diagnóstico. Atributo cuyos niveles configurados se comprueban como bandas horizontales.",
      quantityRole:
        "Normalización y resolutor. Atributo tratado como cantidad/tamaño para familias y orden de tamaños.",
      terminalVariantRole:
        "Resolutor y puntuación. Atributo usado para el orden de variante terminal y valores finales.",
      useFirstRegex:
        "Activa la regex inicial guardada. Desactivarla no aplica ninguna regla implícita de Original o Regular.",
      firstRegex:
        "Desempate de inferencia. Solo adelanta coincidencias cuando el estante no aporta un orden.",
      useLastRegex:
        "Activa la regex final guardada. Desactivarla no aplica reglas implícitas de Sensitive o Free & Clear.",
      lastRegex:
        "Orden de valores. Lleva coincidencias al final; no elige el nivel del atributo en el árbol.",
      normalizationEnabled:
        "Activa análisis de cantidades, enriquecimiento por nombre y conciliación de familias para el rol de cantidad.",
      inferFromName:
        "Usa cantidades confirmadas del nombre para completar valores ausentes o números sin unidad.",
      zeroIsMissing: "Trata el cero numérico como ausente y permite recuperarlo desde el nombre.",
      liquidOuncesAreFluid:
        "Interpreta onzas ambiguas de líquidos como onzas fluidas y separa volumen de masa.",
      unitPreference:
        "Gana la primera unidad coincidente cuando el nombre contiene varias cantidades; no se añade un orden oculto.",
      liquidNameTerms:
        "Términos del nombre que identifican líquidos al decidir si las onzas son fluidas.",
      minimumFamilyEvidence:
        "Valores inequívocos necesarios para unir una cantidad ambigua a una familia. Un valor mayor es más conservador.",
      reconciliationMinimum:
        "Límite inferior inclusivo de proporción para unir valores ambiguos a una familia de volumen.",
      reconciliationMaximum:
        "Límite superior inclusivo de proporción para unir valores ambiguos a una familia de volumen.",
      minFacingsPerShelf:
        "Movimientos. Frentes por estante necesarios para tratar un valor movido como bloque vertical completo.",
      roundTripFloorShare:
        "Movimientos. Proporción mínima de productos reproducidos en el mismo estante tras regenerar la jerarquía.",
      fillFloorShare:
        "Movimientos. Ocupación mínima objetivo por lineal al cerrar huecos después de mover.",
      fillDropTolerancePts:
        "Movimientos. Puntos porcentuales de ocupación que se permite perder frente al estado anterior.",
      seamPass:
        "Reparación posterior: off la omite, facings-neutral la permite sin cambiar frentes y on permite la pasada completa.",
      blockShare:
        "Umbral del resolutor. Cobertura de estantes necesaria para considerar un valor bloque vertical; subirlo exige más cobertura.",
      fragmentJoinPct:
        "Inferencia directa. Hueco máximo a lo largo de un estante aún tratado como un fragmento, como porcentaje del ancho del estante del módulo; subirlo oculta más huecos pequeños.",
      minFillRatio:
        "Solo reparación. La corrección vertical rechaza leer alineaciones por debajo de esta ocupación.",
      preserveRange:
        "Solo reparación. Impide que la corrección vertical retire el último frente de un SKU.",
      onlyIfBetter:
        "Solo reparación. Conserva la corrección vertical únicamente si mejora la puntuación estética compuesta.",
      brandAttribute:
        "Es el mismo ajuste que Rol de marca. Agrupa el bloqueo vertical y comprueba marcas eliminadas.",
      tierAttribute:
        "Es el mismo ajuste que Rol de nivel. Los valores se evalúan con el orden configurado; sin orden, la medida se desactiva.",
      sizeAttribute:
        "Es el mismo ajuste que Rol de cantidad. Alimenta el orden de tamaños y el peso de volumen derivado.",
      flavourAttribute:
        "Es el mismo ajuste que Rol de variante terminal. Alimenta las comprobaciones de variante y valor final.",
      verticalAttributes:
        "Es el mismo ajuste que Orden de roles verticales. Define la ruta agrupada como bloque vertical.",
      tierLabel:
        "Nombre visible del nivel. La posición de la fila, no el texto, define el orden de arriba abajo.",
      tierValues:
        "Valores del nivel sin distinguir mayúsculas. Los valores no listados no se evalúan como nivel.",
      lastInBlock:
        "Puntuación de orden. Los valores o nombres con estos términos deben cerrar su bloque; una lista vacía no aplica reglas implícitas.",
      bayOwners:
        "Puntuación de bloqueo. Los bloques coincidentes también deben alinear sus bordes con módulos completos.",
      bulkPounds:
        "Puntuación ergonómica. Peso derivado mínimo considerado voluminoso; 0 desactiva esta evaluación.",
      lowerShelfShare:
        "Puntuación ergonómica. Fracción de estantes inferiores considerada zona de elevación para productos pesados.",
      jitterPacks:
        "Puntuación de bloqueo. Anchuras de paquete perdonadas antes de penalizar bordes verticales.",
      raggedZeroPacks:
        "Puntuación de bloqueo. Irregularidad excedente, en paquetes, donde un bloque vertical llega a cero.",
      flagsPerHundredZero:
        "Detector CDT. Alertas ponderadas por 100 SKUs donde la puntuación del detector llega a cero.",
      blockingPenalty: "Penalización del detector para cada alerta de gravedad bloqueante.",
      toCheckPenalty: "Penalización del detector para cada alerta por comprobar.",
      toReviewPenalty: "Penalización del detector para cada alerta por revisar.",
      blockingWeight: "Peso del bloqueo en la puntuación total. Todos los pesos deben sumar 100.",
      cdtWeight:
        "Peso del cumplimiento CDT en la puntuación total. Todos los pesos deben sumar 100.",
      fullnessWeight:
        "Peso de huecos y salientes en la puntuación total. Todos los pesos deben sumar 100.",
      ergonomicsWeight:
        "Peso de colocar los paquetes grandes debajo y los pesados abajo. Todos los pesos deben sumar 100.",
      rulesKeptWeight:
        "Peso de cumplir las reglas de estante aceptadas. Todos los pesos deben sumar 100.",
      rangeWeight:
        "Peso de colocar todo el surtido y conservar cada marca. Todos los pesos deben sumar 100.",
      shapeSplit:
        "Parte del bloqueo asignada a la forma de cada bloque: una sola pieza que llena su rectángulo. Forma, bordes rectos y escalera deben sumar 1.",
      edgesSplit:
        "Parte del bloqueo asignada a los bordes rectos: las franjas en sus bordes izquierdo y derecho, las bandas en su borde superior e inferior. Forma, bordes rectos y escalera deben sumar 1.",
      ladderSplit:
        "Parte del bloqueo asignada a la escalera de niveles, cuando está configurada. Forma, bordes rectos y escalera deben sumar 1.",
      detectorSplit:
        "Parte del CDT asignada a alertas del detector; detector y orden deben sumar 1.",
      orderingSplit: "Parte del CDT asignada al orden; detector y orden deben sumar 1.",
      fullProfileJson:
        "Perfil completo validado, incluidas reglas y asignaciones exclusivas del JSON. Los campos sencillos reescriben este texto.",
    },
    advancedReference: {
      title: "Propiedades exclusivas del JSON",
      sectionSemantics:
        "Remapeo por sección, condiciones y niveles omitidos. Gana la primera asignación coincidente.",
      overlayAttr: "Atributo activo representado por un nivel del perfil.",
      unitAliases:
        "Grafías de unidades, sin distinguir mayúsculas, asignadas a unidades canónicas.",
      reconciliationBlockAttributes:
        "Atributos que definen grupos de evidencia de cantidad; false desactiva la conciliación.",
      firstValueRules:
        "Subcadenas por atributo preferidas al inicio del orden de valores hermanos.",
      lastValueRules:
        "Subcadenas por atributo preferidas al final; la regla terminal también alimenta la puntuación de cierre.",
    },
    help: {
      ignoredAttributes: "Nunca un nivel de CDT: {{attributes}}",
      attributes:
        "Este es el orden de preferencia de candidatos guardado; la geometría del estante aún decide la jerarquía.",
      commaSeparated: "Separa los valores con comas; se conservará su orden.",
      advanced:
        "Aquí están disponibles todos los valores del perfil, incluidas las reglas de valores y los alias de unidades. Un JSON de perfil válido actualiza los campos sencillos; al cargar siempre se valida y compila el perfil completo.",
    },
    errors: {
      saveFailed: "No se pudo guardar la configuración. Inténtalo de nuevo.",
      title: "Corrige estos problemas antes de cargar",
      invalidJson: "El editor de perfil completo no contiene un JSON válido.",
      persistence:
        "No se pudo guardar la configuración en este navegador. Comprueba el almacenamiento disponible.",
    },
    saved: "Configuración guardada para esta categoría.",
    fragmentJoinLegacy: "Valor heredado {{value}}, sin convertir",
  },
  configuration: {
    settingsLoadFailed: "No se pudo cargar la configuración del comercio. Inténtalo de nuevo.",
    accessDenied: "No tienes permiso para editar la configuración de CDT.",
    archivedCategory: "Las categorías archivadas no pueden activar la configuración de CDT Studio.",
    invalidCategory: "Categoría no válida.",
    loadFailed: "No se pudo cargar esta categoría.",
    loading: "Cargando la configuración de CDT Studio…",
    defaultProfile:
      "Se parte del perfil predeterminado para {{merchant}}. Revisa la asignación de atributos antes de guardar.",
    unsavedDescription:
      "Tienes cambios sin guardar en esta configuración de CDT Studio. Guárdalos antes de salir o sal de todos modos y perderás los cambios.",
    unsavedSaveFailed: "No se pudo guardar la configuración de CDT Studio",
  },
  /** The list's counts, in the words its cards use. */
  severityCount: {
    blocking_one: "1 problema",
    blocking_other: "{{count}} problemas",
    toCheck_one: "1 comprobación",
    toCheck_other: "{{count}} comprobaciones",
    toReview_one: "1 revisión",
    toReview_other: "{{count}} revisiones",
  },
  /** A card's chip, in the words the counts use. */
  severityChip: {
    blocking: "Problema",
    toCheck: "Comprobación",
    toReview: "Revisión",
  },
  toolbar: {
    undo: "Deshacer",
    redo: "Rehacer",
    fixBlocking: "Corregir bloqueo vertical",
    fixAll: "Corregir todo",
    fixAllStarting: "Corrigiendo…",
    fixAllProgress_one: "Corrigiendo… 1 corrección probada",
    fixAllProgress_other: "Corrigiendo… {{count}} correcciones probadas",
    weighingMove: "Evaluando el movimiento…",
    waitForFixAll: "Espera a que termine Corregir todo",
    waitForMove: "Espera a que se evalúe el movimiento",
    finalize: "Finalizar CDTs",
    downloadCsv: "Guardar CDTs en archivo",
    more: "Más",
  },
  footer: {
    finalize: "Finalizar",
    waiting_one: "Falta 1 respuesta",
    waiting_other: "Faltan {{count}} respuestas",
    waitingThen:
      "Después, Finalizar escribe las reglas con las que se construye cada planograma de tienda; antes no se pueden generar planogramas de tienda.",
    layoutOf: "la disposición de {{brand}}",
    splitOf: "la separación de {{name}}",
    bands_one: "la franja de estantes",
    bands_other: "las {{count}} franjas de estantes",
    sizeTags_one: "la etiqueta de tamaño",
    sizeTags_other: "las {{count}} etiquetas de tamaño",
    ready: "Listo para finalizar",
    ships: "Se envían {{sections}}, {{brands}} y {{bands}}.",
    shipsNoBrands: "Se envían {{sections}} y {{bands}}.",
    sections_one: "1 sección",
    sections_other: "{{count}} secciones",
    brandBlocks_one: "1 bloque de marca",
    brandBlocks_other: "{{count}} bloques de marca",
    shelfBands_one: "1 franja de estantes",
    shelfBands_other: "{{count}} franjas de estantes",
    finalized: "Finalizado",
    finalizedDetail:
      "Ya se pueden generar los planogramas de tienda con las reglas que envió Studio.",
  },
  provenance: {
    noNewBreaks: "Sin nuevas divisiones — tus cambios no rompieron nada.",
    regenDelta:
      "La regeneración reproduce {{exact}}/{{total}} productos en su estante exacto — las excepciones de esta vista previa pueden ser artefactos de la regeneración, no tus cambios",
    regenShed: "el plan regenerado lleva {{count}} frente(s) cedido(s)",
    regenOverflow:
      "{{lanes}} estante(s) llevan {{length}} más que su lineal — los productos se comprimen y pueden solaparse",
    newBreaks: "{{count}} bloques rotos por tus cambios",
    moved: "{{count}} SKUs movidos vs base",
  },
  evidence: {
    built: "Construido: {{order}}",
    want: "Esperado: {{order}}",
    fix: "Corregir",
    acceptNamesVariant:
      "Mantiene {{variant}} en estos estantes como regla de ubicación para las tiendas de este flujo.",
    acceptNoRow:
      "Mantiene esta diferencia tal como está. Sus productos están en más de una sección, así que no se guarda ninguna regla de ubicación.",
    accept: "Aceptar como excepción",
    dismiss: "Descartar",
  },
  resolver: {
    blockingTooSparse:
      "Bloqueo vertical no aplicado: las estanterías están llenas solo al {{fill}}%, y la corrección lee sus bandas del plan tal como está. Primero llena el plan (o regenéralo) y luego cuadra los bloques.",
    blockingFixed: "Bloqueo vertical: puntuación {{scoreBefore}} → {{scoreAfter}}.",
    blockingFixedBeforeFixes:
      "Bloqueo vertical: puntuación {{scoreBefore}} → {{scoreAfter}}. Primero se cuadraron los bloques y luego se volvieron a corregir las tarjetas.",
    blockingFixedAdded_one: "1 frente añadido",
    blockingFixedAdded_other: "{{count}} frentes añadidos",
    blockingFixedRemoved_one: "1 frente retirado",
    blockingFixedRemoved_other: "{{count}} frentes retirados",
    blockingFixedTraded: "{{added}} y {{removed}}",
    blockingFixedOn_one: "{{change}} en 1 producto",
    blockingFixedOn_other: "{{change}} en {{count}} productos",
    blockingFixedDropped_one: "1 producto fuera del plan",
    blockingFixedDropped_other: "{{count}} productos fuera del plan",
    blockingFixedRagged: "{{ragged}} de borde irregular restante",
    blockingFixedResiduals_one: "1 bloque aún fuera de línea",
    blockingFixedResiduals_other: "{{count}} bloques aún fuera de línea",
    blockingHarms:
      "Bloqueo vertical no aplicado: partiría un bloque o fallaría una comprobación que el plan pasa ahora. No se movió nada.",
    blockingNoGain:
      "Bloqueo vertical no aplicado: puntuaría {{scoreAfter}} frente a {{scoreBefore}} ahora. No se movió nada.",
    blockingNoGainSameScore:
      "Bloqueo vertical no aplicado: dejaría la puntuación en {{scoreBefore}}. No se movió nada.",
    blockingUnscored:
      "Bloqueo vertical no aplicado: el plan resultante no se puede puntuar. No se movió nada.",
    blockingBelowDos:
      "Bloqueo vertical no aplicado: dejaría un producto por debajo de sus días de inventario. No se movió nada.",
    blockingTooTall:
      "Bloqueo vertical no aplicado: pondría un producto en un estante sin la altura necesaria para él. No se movió nada.",
    cannotRepairInPlace: "Sin corrección automática",
    cannotRepairInPlaceDetail:
      "Ninguna corrección de esta tarjeta sube por sí sola la puntuación mostrada.",
    cannotRepairInPlaceDetailUnscored:
      "Ninguna corrección resuelve esta tarjeta. Consérvala o descártala.",
    cannotRepairBelowDos:
      "No hay corrección sin dejar un producto por debajo de sus días de inventario.",
    fixAllDone_one:
      "Corregir todo: puntuación {{scoreBefore}} → {{scoreAfter}}, 1 corrección: {{fixes}}.",
    fixAllDone_other:
      "Corregir todo: puntuación {{scoreBefore}} → {{scoreAfter}}, {{count}} correcciones: {{fixes}}.",
    fixAllDoneRepeats_one:
      "Corregir todo: puntuación {{scoreBefore}} → {{scoreAfter}}, {{count}} correcciones, todas iguales: {{fixes}}.",
    fixAllDoneRepeats_other:
      "Corregir todo: puntuación {{scoreBefore}} → {{scoreAfter}}, {{count}} correcciones, {{distinct}} distintas: {{fixes}}.",
    fixAllMore: "{{names}} y {{count}} más",
    fixAllGathered: "{{block}}: {{count}} bloques reunidos",
    flowSort: "Orden de flujo",
    flowCardTitle: "{{block}} fuera de orden",
    flowCardDetail: "Sus productos no siguen el orden acordado a lo largo del estante. Ordénalos.",
    piecesCardTitle: "{{block}} partido en {{count}} bloques",
    piecesCardDetail_one:
      "Parte del bloque queda aparte en el estante {{shelves}}. Muévela junto al resto para que se lea como un solo bloque.",
    piecesCardDetail_other:
      "Parte del bloque queda aparte en los estantes {{shelves}}. Muévela junto al resto para que se lea como un solo bloque.",
    fillShelves: "Llenar estantes",
    fillCardTitle_one: "El estante {{shelves}} tiene huecos",
    fillCardTitle_other: "Los estantes {{shelves}} tienen huecos",
    fillCardDetail_fixable:
      "Sus productos no llegan de punta a punta. Añade frentes a los productos que ya están ahí.",
    fillCardDetail_maxFacings:
      "Cada producto ya tiene el máximo de frentes. Añade productos para llenarlo.",
    fillCardDetail_noRoom:
      "El hueco que queda es más estrecho que cualquier producto del estante. Añade un producto más estrecho para llenarlo.",
    fillCardDetail_harm: "Más frentes aquí bajarían la puntuación. Llénalo a mano.",
    fixAllWholePlan: "el plan",
    fixAllNoGain:
      "Corregir todo: no queda nada que suba la puntuación ({{score}}). No se movió nada.",
    fixAllFailed: "Corregir todo no pudo ejecutarse: {{reason}}",
    treeEditHarms:
      "Árbol sin cambios: el plan construido desde el árbol partiría un bloque o fallaría una comprobación que el plan pasa ahora.",
    treeEditUnscored: "Árbol sin cambios: el plan construido desde el árbol no se puede puntuar.",
    treeEditNotMade:
      "Árbol sin cambios: ya está en ese lugar, así que el orden en el estante seguiría igual.",
    autoResolve: "Resolver automáticamente",
    resetFixes: "Restablecer correcciones",
    repositioned: "{{count}} frentes reubicados respecto a la base",
    regenerated:
      "Plan regenerado desde el árbol — cada bloque sigue el orden del árbol; las excepciones restantes se miden sobre el layout regenerado",
    auditStrip:
      "pares invertidos {{inverted}} · cortes de carril {{laneBreaks}} · bloques divididos {{splits}}",
    gradeTitle: "Gradiente de tamaño · todos los bloques",
    gradeSkipped: "{{node}} — {{why}}",
    gradePinned: "{{node}}: {{id}} retenido arriba — demasiado alto para el estante inferior",
    suggested: "Resolución sugerida:",
    suggestion: {
      relocate:
        "Resolver automáticamente — devolver los sueltos a su bloque, comprando espacio con frentes.",
      resequence: "Resolver automáticamente — reordenar el bloque según la secuencia del árbol.",
      retag: "Cambio de datos / árbol — reetiquetar los SKUs; nada se mueve en el estante.",
    },
  },
  flagWords: {
    and: " y ",
    shelves_one: "el estante {{shelves}}",
    shelves_other: "los estantes {{shelves}}",
    shelfRange: "los estantes {{from}}–{{to}}",
    whereShelf: "el estante {{shelf}}",
    whereAisle: "otro pasillo, estante {{shelf}}",
    thisPlan: "Este plan",
    thisBlock: "este bloque",
    splitTitle: "{{block}} partido en {{count}} bloques",
    splitDetail:
      "Otros productos interrumpen este bloque. Sácalos para que se lea como un solo bloque.",
    bandHoleTitle: "{{block}} se salta {{holes}}",
    bandHoleDetail:
      "Está en {{occupied}} pero falta en {{holes}}. Apílalo en estantes contiguos como un solo bloque.",
    scatterTitle: "{{block}} en {{count}} lugares",
    scatterDetail:
      "Sus frentes están repartidos con otros productos en medio. Júntalos en un solo bloque.",
    contiguityTitle_one: "{{blocks}} parte el bloque {{block}}",
    contiguityTitle_other: "{{blocks}} parten el bloque {{block}}",
    contiguityDetail_one:
      "1 frente de otro bloque está dentro de este bloque en {{shelves}}. Sácalo.",
    contiguityDetail_other:
      "{{count}} frentes de otros bloques están dentro de este bloque en {{shelves}}. Sácalos.",
    fragmentTitle_one: "{{block}}: 1 frente fuera de su bloque ({{where}})",
    fragmentTitle_other: "{{block}}: {{count}} frentes fuera de su bloque ({{where}})",
    fragmentDetail_one:
      "El bloque está en {{span}}; 1 frente queda aparte en {{where}}. Muévelo al bloque.",
    fragmentDetail_other:
      "El bloque está en {{span}}; {{count}} frentes quedan aparte en {{where}}. Muévelos al bloque.",
    wrapTitle: "{{lower}} rodea a {{upper}} ({{value}})",
    wrapDetail:
      "{{lower}} está al lado y debajo de {{upper}}; suele hacerse a propósito para dar una esquina a una marca más pequeña. Mantenlo si es intencionado.",
    columnTitle: "{{value}} repartido entre {{upper}} y {{lower}}",
    columnDetail:
      "{{value}} está en {{upperShelves}} y otra vez en {{lowerShelves}}, con {{between}} en medio. Sube el {{value}} de abajo junto al resto.",
    blockOnShelves: "{{block}} → {{shelves}}",
    wrapWant: "{{value}} tal como está, si es intencionado",
    columnWant: "{{value}} en un solo bloque",
    orphanAisleTitle: "{{product}} en otro pasillo que su bloque {{block}}",
    orphanBaysTitle: "{{product}} a {{bays}} módulos de su bloque {{block}}",
    orphanDetail: "Sus dos vecinos son de otros bloques. Devuélvelo a su propio bloque.",
    sizeOrderTitle: "{{block}}: tamaños fuera de orden",
    sizeOrderLabelTitle: "{{block}}: {{label}} fuera de orden",
    pairsOutOfOrder_one: "1 par fuera de orden en el estante",
    pairsOutOfOrder_other: "{{count}} pares fuera de orden en el estante",
    outOfPlace: "fuera de lugar: {{values}}",
    biggerAbove_one: "1 lugar donde un empaque más grande está encima de uno más pequeño",
    biggerAbove_other:
      "{{count}} lugares donde un empaque más grande está encima de uno más pequeño",
    flowTitle: "{{block}}: {{label}} fuera de flujo",
    pairsOutOfFlow_one: "1 par fuera de flujo",
    pairsOutOfFlow_other: "{{count}} pares fuera de flujo",
    strayFacing: "Frente suelto: {{values}}",
    ambiguousTitle: '"{{value}}" usado en dos niveles ({{levels}})',
    ambiguousDetail:
      "El mismo nombre en dos niveles: {{uses}}. Una etiqueta está mal; corrígela en el maestro de productos. Nada se mueve en el estante.",
    dataGapTitle: "Falta {{label}} en los datos de producto",
    dataGapDetail:
      "Los tamaños están en orden dentro de cada {{level}} ({{values}}), pero los datos no tienen {{label}}. Completa {{label}} en estos productos en el origen.",
    inOrderTitle: "Tamaños en orden dentro de cada {{level}}",
    inOrderDetail:
      "Los tamaños están en orden dentro de cada uno de {{values}}. Solo parecen fuera de orden si se leen entre grupos. No hace falta ningún cambio.",
    groupFirstTitle: "Agrupar por {{level}} antes que {{over}} en {{scope}}",
    groupFirstDetail:
      "Los tamaños están en orden dentro de cada {{level}} ({{values}}). La agrupación lo pone bajo {{over}}, un nivel más abajo; ponlo primero para que coincida con el estante.",
    groupFirstFix: "Agrupar por {{label}} antes que {{next}} dentro de {{node}}",
    splitByTitle: "Separar {{scope}} por {{level}}",
    splitByDetail:
      "Los tamaños están en orden dentro de cada {{level}} ({{values}}). Revisa si {{node}} debería agruparse por {{level}}.",
    splitByFix: "Separar {{node}} por {{label}}",
    topRow: "superior",
    bottomRow: "inferior",
    rowLineTitle_one:
      '{{block}}: sus filas no reproducen el planograma base; el producto etiquetado como {{attribute}} "{{value}}" forma la fila {{side}} → "{{line}}" como fila propia (1 SKU)',
    rowLineTitle_other:
      '{{block}}: sus filas no reproducen el planograma base; los {{count}} productos etiquetados como {{attribute}} "{{value}}" forman la fila {{side}} → "{{line}}" como fila propia ({{count}} SKU)',
    rowLineDetail:
      'Según las filas que interpreta Studio, {{block}} no se apila como en el planograma base. Allí, los productos etiquetados como {{attribute}} "{{value}}" forman la fila {{side}}. El planograma de tienda mantiene "{{line}}" allí en una fila propia.',
    rowLineFix: 'Convertir "{{line}}" en una fila propia',
    rowQuestionTitle:
      "{{block}}: sus filas no reproducen el planograma base y ninguna línea de productos explica cómo se apila",
    rowQuestionDetail:
      "Según el orden de filas de Studio ({{order}}), {{block}} no se apila como en el planograma base: allí, {{rows}}. Ningún valor de atributo identifica los productos que aparecen separados en el planograma base. Revise el maestro de productos o indique a Studio cómo disponer este bloque.",
    rowOnShelves: "{{row}} en {{shelves}}",
    rowMissing: "productos sin fila",
    sizeTierLine: "{{brand}}, tamaño {{size}}",
    sizeTierTitle_one:
      "{{line}} (1 producto) está etiquetado como {{from}}, pero en el planograma base está con los productos {{to}} → tratarlo como {{to}}",
    sizeTierTitle_other:
      "{{line}} ({{count}} productos) están etiquetados como {{from}}, pero en el planograma base están con los productos {{to}} → tratarlos como {{to}}",
    sizeTierDetail:
      "En el planograma base, {{line}} está en {{where}} con los productos {{to}} de {{brand}}; los demás productos {{from}} de {{brand}} están en {{ownWhere}}. Al tratarlo como {{to}}, el planograma de tienda lo mantiene en la fila {{to}}.",
    sizeTierFix: "Tratar {{line}} como {{to}}",
    sizeLayoutTitle:
      "{{brand}}: en el planograma base sus tamaños van de pequeño a grande y las submarcas {{subBrands}} se alternan estante por estante. ¿Cómo deben disponerlo los planogramas de tienda?",
    sizeLayoutTitleUnsorted:
      "{{brand}}: en el planograma base las submarcas {{subBrands}} se alternan estante por estante. ¿Cómo deben disponerlo los planogramas de tienda?",
    sizeLayoutDetail:
      "En el planograma base, las submarcas de {{brand}} no ocupan estantes propios ni se colocan lado a lado como bloques verticales, por lo que Studio no puede determinar la disposición prevista. Mantenga el orden de productos del planograma base, use bloqueo horizontal (cada submarca en estantes propios) o bloqueo vertical (cada submarca en su propio bloque vertical).",
  },
  inference: {
    conventionChip: "Convención ({{pct}})",
    exceptionsTitle: "Excepciones de inferencia ({{count}})",
    exceptionChip: "Excepción",
    alertChip: "Alerta",
    kind: {
      "orient-disagrees": "orientación",
      "no-order": "sin orden inferible",
      "contrary-block": "contra la corriente",
      "order-drift": "deriva de orden",
      "gradient-section": "gradiente de sección",
      "size-unparsed": "tamaño imputado",
      "unit-mix": "familias de unidades mezcladas",
      "pin-backwards": "el defecto reclama un orden inverso",
    },
  },
  panel: {
    closeDirtyTitle: "¿Cerrar el estudio y perder tu trabajo?",
    shippedBlockingTitle: "CDT finalizados",
    shippedBlocking:
      "Los planogramas de tienda usarán bloqueo vertical por defecto, con la versión {{releaseId}}.",
    shippedBlockingHorizontal:
      "Los planogramas de tienda usarán bloqueo horizontal por defecto, con la versión {{releaseId}}.",
    closeWithoutRelease:
      "Los CDT no están finalizados para este planograma, así que los planogramas de tienda se bloquearán en horizontal.",
    closeDirtyDescription:
      "Los descartes, excepciones aceptadas, correcciones aplicadas y ediciones del árbol aún no se han guardado. Al cerrar se pierden.",
    closeKeepEditing: "Seguir editando",
    closeDiscard: "Descartar y cerrar",
    close: "Cerrar",
    location: "Módulo {{bay}} · Estante {{shelf}}",
    previewRegenerated: "Previsualizar el plan construido desde el árbol",
    previewHarms:
      "El plan construido desde el árbol partiría un bloque o fallaría una comprobación que el plan pasa ahora.",
    previewUnscored: "El plan construido desde el árbol no se puede puntuar.",
    previewMatches: "El plan ya coincide con el árbol.",
    wallReading: {
      readsRows: "Este mueble se lee como bloqueo horizontal.",
      readsColumns: "Este mueble se lee como bloqueo vertical.",
      setRows:
        "Usted fijó este mueble en bloqueo horizontal. El planograma base se lee como bloqueo vertical.",
      setColumns:
        "Usted fijó este mueble en bloqueo vertical. El planograma base se lee como bloqueo horizontal.",
      storeUsesRows:
        "Los planogramas de tienda generados con esta versión usan bloqueo horizontal.",
      readAsRows: "Usar bloqueo horizontal",
      readAsColumns: "Usar bloqueo vertical",
      flipHint: "(primero muestra una vista previa; cambia lo que se envía)",
      closeCall: "Resultado ajustado: revíselo.",
      brandShelves: {
        label: "Estantes por marca:",
        whereClose: "Estante completo si las ventas de la marca casi lo llenan",
        full: "Cada marca ocupa estantes completos",
        sales: "Marcas según sus ventas",
        help: "Cómo los planogramas de tienda dan sus estantes a cada marca. Estante completo si las ventas de la marca casi lo llenan: una marca cuyas ventas casi llenan un estante recibe el estante entero. Cada marca ocupa estantes completos: todas las marcas reciben estantes enteros, aun por encima de sus ventas. Marcas según sus ventas: cada marca recibe su parte de las ventas, así que dos marcas pueden compartir un estante.",
      },
      detail:
        "Puntuación de bloqueo vertical de {{attr}}: {{score}} (vertical desde {{threshold}}); de bloqueo horizontal: {{band}} (horizontal desde {{bandThreshold}}). {{share}}% de los SKU están en grupos de {{attr}} apilados entre estantes (horizontal desde {{shareThreshold}}%).",
    },
    questions: {
      headerNone: "Nada que decidir",
      headerOne: "1 cosa que decidir",
      headerMany: "{{count}} cosas que decidir",
      numbered: "{{number}} · {{title}}",
      collapseCard: "Contraer esta pregunta",
      expandCard: "Expandir esta pregunta",
      confirm: "Confirmar",
      sizeTagsTitle_one: "Una etiqueta de tamaño que revisar.",
      sizeTagsTitle_other: "{{count}} etiquetas de tamaño que revisar.",
      sizeTagsLine:
        "{{line}} está etiquetado {{from}}, pero el planograma base lo coloca con los paquetes {{to}}. El estante se queda como en el base; tú decides la etiqueta.",
      sizeTagsMore: "Lo mismo para {{lines}}.",
      sizeTagsOpen: "Decidir las etiquetas",
      sizeTagsClose: "Ocultar las etiquetas",
      splitTitle: "{{newBrand}} está lejos del resto de {{brand}}. ¿Separarla como marca propia?",
      splitTitleEdge:
        "{{newBrand}} forma su propio bloque vertical junto al resto de {{brand}}. ¿Separarla como marca propia?",
      run: "tramo {{number}}",
      locationJoin: " y ",
      splitWhereOne: "1 producto en {{where}}. El resto de {{brand}} está en {{mainWhere}}.",
      splitWhereMany:
        "{{count}} productos en {{where}}. El resto de {{brand}} está en {{mainWhere}}.",
      splitYes: "Sí, separarla",
      splitNo: "No, mantenerla como {{brand}}",
      pairTitle:
        "{{first}} y {{second}} están juntas, lejos de sus marcas. ¿Hacer de cada una su propia marca?",
      pairWhereEachOne: "1 producto cada una, {{where}}.",
      pairWhereEachMany: "{{count}} productos cada una, {{where}}.",
      pairWhere: "{{firstCount}} y {{secondCount}} productos, {{where}}.",
      pairYes: "Sí, separar ambas",
      pairNo: "No, mantenerlas",
      sizeTierTitleOne:
        "{{line}} (1 producto) está marcado como {{from}}, pero en el planograma base está con los {{to}}. ¿Tratarlo como {{to}}?",
      sizeTierTitleMany:
        "{{line}} ({{count}} productos) está marcado como {{from}}, pero en el planograma base está con los {{to}}. ¿Tratarlo como {{to}}?",
      sizeTierWhere:
        "En el planograma base está en {{where}} con los {{to}}. Los demás {{from}} de {{brand}} están en {{mainWhere}}.",
      sizeTierYes: "Sí, tratarlo como {{to}}",
      sizeTierNo: "No, mantenerlo {{from}}",
      sizeTierDone: "{{line}} ahora es {{to}}",
      sizeLayoutTitle:
        "{{brand}}: en el planograma base sus tamaños van de pequeño a grande y las submarcas {{subBrands}} se alternan estante por estante. ¿Cómo deben disponerlo los planogramas de tienda?",
      sizeLayoutTitleUnsorted:
        "{{brand}}: en el planograma base las submarcas {{subBrands}} se alternan estante por estante. ¿Cómo deben disponerlo los planogramas de tienda?",
      sizeLayoutLadder: "Mantener el orden de productos del planograma base",
      sizeLayoutHorizontal:
        "Bloqueo horizontal: cada submarca en estantes propios ({{order}}, como en el planograma base)",
      sizeLayoutVertical: "Bloqueo vertical: cada submarca en su propio bloque vertical",
      sizeLayoutAbove: " sobre ",
      listAnd: " y ",
      sizeLayoutDoneLadder: "{{brand}} mantiene el orden de productos del planograma base",
      sizeLayoutDoneHorizontal: "{{brand}}: bloqueo horizontal, cada submarca en estantes propios",
      sizeLayoutDoneVertical:
        "{{brand}}: bloqueo vertical, cada submarca en su propio bloque vertical",
      sizeLayoutFirst: "Primero responda cómo disponer {{brand}}",
      change: "Cambiar",
      showMe: "Muéstramelo en el mueble",
      splitDone: "{{newBrand}} ya es una marca propia",
      pairDone: "{{first}} y {{second}} ya son marcas propias",
      undo: "Deshacer",
      hiddenOne: "1 pregunta oculta",
      hiddenMany: "{{count}} preguntas ocultas",
      show: "Mostrar",
    },
    tidyUps: {
      summaryOne: "1 ajuste que Studio resuelve solo",
      summaryMany: "{{count}} ajustes que Studio resuelve solo",
      forBrand: "{{count}} de {{brand}}",
      others: "{{count}} otros",
      detail: "nada que decidir",
    },
    exception: {
      oneFlag: "{{title}} ({{location}}).",
      manyOutside: "{{count}} productos están fuera de su bloque.",
      manyOther: "{{count}} excepciones que Studio puede corregir: {{titles}}.",
      doGap: "Studio puede cerrar el hueco en las reglas que envía",
      doOutside: "Studio puede moverlo junto a su bloque",
      doOther: "Studio puede corregirlo en las reglas que envía",
      doOutsideMany: "Studio puede moverlos junto a sus bloques",
      doMany: "Studio puede corregirlas en las reglas que envía",
      tail: "el planograma base no cambia, y Deshacer lo revierte. Finalizar no espera a esto.",
      moveIt: "Moverlo",
      moveThemBack: "Devolverlos a su sitio",
      fixIt: "Corregirlo",
      fixThem: "Corregirlas",
    },
    loadingAttributes: "Cargando el maestro de artículos...",
    attributesFailed: "No se pudieron cargar los atributos del maestro desde la base de datos.",
    loadingLengthUnit: "Cargando la unidad de la categoría...",
    lengthUnitFailed:
      "No se pudo cargar la unidad de la categoría, así que Studio aún no puede indicar medidas.",
    retry: "Reintentar",
    shipping: "Guardando los CDT...",
    shipped:
      "Se guardaron {{count}} productos en la tabla CDT — árbol {{treeId}}, versión {{releaseId}}",
    storeRange_one:
      "1 producto de tus tiendas no está en la base. Lo puse junto a su producto más parecido.",
    storeRange_other:
      "{{count}} productos de tus tiendas no están en la base. Puse cada uno junto a su producto más parecido.",
    storeRangeProduct: "{{name}} — junto a {{neighbour}}",
    shipNoMerchflow:
      "No hay un merchflow en contexto, así que no existe un árbol de decisión donde guardar. Abre el estudio desde el planograma de un merchflow.",
    storeRulesRequired:
      "No se puede guardar este CDT porque sus medidas por tienda no coinciden con el árbol mostrado en CDT Studio. Corrige el árbol e inténtalo de nuevo.",
    shippedWithGaps:
      "Se guardaron {{count}} productos en la tabla CDT — árbol {{treeId}}, versión {{releaseId}}. {{dropped}} producto(s) se guardaron sin ubicación y no se generarán.",
    orderChanged:
      "Conviene revisarlo: el CDT guardado no puede mantener todos los bloques verticales, bloques horizontales o grupos en el orden que ves aquí, así que los planogramas generados colocarán algunos en otra secuencia.",
    staleRules:
      "{{count}} regla(s) de ubicación no se guardaron: {{rules}} ya no es una sección de este árbol, así que la regla vaciaría cada estante que nombra.",
    shippedWithoutRules:
      "El CDT está guardado, pero las reglas de ubicación no — nada sujeta esos productos a sus estantes cuando se generen los planogramas.",
    unplacedTitle: "{{count}} producto(s) tienen CDT pero ningún lugar en este planograma:",
    unplacedCombo: "{{cdt0}} / {{cdt1}}",
    unplacedProduct: "· {{name}} ({{productCode}})",
    unplacedConsequence:
      "Si continúas se guarda el CDT, pero el generador no colocará estos {{count}} producto(s) en ningún planograma.",
    unplacedContinue: "Finalizar de todos modos",
    gateTitle: "¿Guardar las reglas de ubicación para los planogramas de tienda?",
    gateOwnsShelf: "{{variant}} se queda en el estante {{shelves}}",
    gateOwnsShelves: "{{variant}} se queda en los estantes {{shelves}}",
    gateNoCost: "No hay que mover nada más: ningún otro producto sale de un estante.",
    gateEvicts:
      "{{evictions}} colocación(es) existente(s) en {{siblings}} grupo(s) de productos más tendrían " +
      "que moverse.",
    gateCarried: "{{count}} grupo(s) existente(s) se conservan sin cambios",
    gateOk: "Guardar reglas de ubicación",
    gateCancel: "Cancelar",
    gateRefusalNoRows: "No hay filas de restricción para guardar.",
    gateRefusalNoCategory:
      "Este merchflow no tiene id de categoría, así que no se pueden usar los endpoints de restricciones.",
    gateRefusalCannotRead:
      "No se pudieron cargar las restricciones existentes. Guardar reemplazaría toda la categoría.",
    gateRefusalEmptyStoreScope:
      "Algunos grupos de restricciones no tienen códigos de tienda. Reasígnalos o elimínalos antes de guardar.",
    gateRefusalGeneric: "{{detail}}",
    gateSaveFailed: "Falló el guardado de restricciones: {{message}}",
  },
  moveReport: {
    scoreTitle: "Este movimiento baja la puntuación",
    previewScoreTitle: "Esta vista previa baja la puntuación",
    scoreLine: "Puntuación {{scoreBefore}} → {{scoreAfter}}.",
    scoreStays: "La puntuación se queda en {{score}}.",
    reachTitle: "Corregir todo no se recupera de este movimiento",
    reachLine: "Corregir todo llega a {{after}} desde este árbol; {{before}} sin el movimiento.",
    reachNote: "Corregir todo tras este movimiento llega a {{after}}.",
    reachUnknown: "No se pudo calcular hasta dónde llega Corregir todo tras este movimiento.",
    discardsTitle: "Esto descarta tus correcciones",
    discardsFixes_one: "Esto rehace el plan desde el árbol y descarta la corrección hecha.",
    discardsFixes_other:
      "Esto rehace el plan desde el árbol y descarta las {{count}} correcciones hechas.",
    budgetTitle: "Este movimiento cuesta frentes",
    budgetDescription:
      "Quita {{facings}} frentes ({{pct}}% del plan) y deja unos {{inches}} in de estante vacío. El árbol quedará bien; el estante quedará más vacío.",
    budgetFillTitle: "Este movimiento cuesta llenado",
    budgetFillDescription:
      "Mantiene los frentes pero cuesta {{pts}} puntos de llenado ({{before}}% → {{after}}%), dejando unos {{inches}} in de estante sin cubrir. El árbol quedará bien; el estante quedará más vacío.",
    budgetCancel: "No mover",
    budgetProceed: "Mover igualmente",
    surgical: "Movimiento ejecutado quirúrgicamente — se preserva todo lo que no se pidió cambiar",
    replay: "Movimiento ejecutado por regeneración",
    mechanismSlide: "Bloque reordenado — el plan no se volvió a derivar desde la jerarquía",
    mechanismReband: "Niveles reapilados — el plan no se volvió a derivar desde la jerarquía",
    churn:
      "{{moved}} de {{total}} productos se recorrieron · {{facings}} con frentes redistribuidos",
    churnNone: "cada producto mantuvo su posición exacta",
    mechanismRemeasure:
      "Re-medido en su lugar — el plan sobrevivió al cambio de jerarquía; solo se volvió a leer el contrato",
    mechanismRebuild: "Reconstrucción — todo el plan se derivó de nuevo desde la jerarquía",
    mechanismBranchRebuild:
      "Rama recolocada según su nueva escalera — todo lo demás permanece en su lugar",
    noop: "Sin cambios — el movimiento ya estaba en su lugar",
    aesthetics: "Estética {{before}} → {{after}} (cada plan medido contra su propia jerarquía)",
    fill: "Ocupación {{before}}% → {{after}}%",
    fillWorstRun: "· peor pasillo {{run}}: {{before}}% → {{after}}%",
    fillOverflow: "· desborde {{length}} (contado aparte de la ocupación)",
    expectedSales: "Ventas esperadas sin cambio — no se agregó ni quitó producto",
    expectedSalesDelta: "Ventas esperadas {{before}} → {{after}}",
  },
  hierarchy: {
    blockedVertical: "vertical",
    blockedHorizontal: "horizontal",
    moveLevelUp: "Subir {{label}} un nivel",
    moveLevelDown: "Bajar {{label}} un nivel",
    poolingAmbiguous: "bloqueo ambiguo con {{ratio}} frentes por estante — toca para decidir",
    ambiguousLead: "Bloqueo ambiguo — el estudio adivinó, tú decides (las respuestas persisten):",
    nodeMeta: "{{skus}} · {{exceptions}}",
    nodeSkus_one: "1 SKU",
    nodeSkus_other: "{{count}} SKUs",
    nodeExceptions_one: "1 excepción",
    nodeExceptions_other: "{{count}} excepciones",
    moveEarlier: "Mover {{label}} antes",
    moveLater: "Mover {{label}} después",
    moveEarlierShort: "◀ Antes",
    moveLaterShort: "Después ▶",
    alreadyFirst: "Ya es el primer bloque.",
    alreadyLast: "Ya es el último bloque.",
    alreadyFirstIn: "Ya es el primer bloque de {{parent}}.",
    alreadyLastIn: "Ya es el último bloque de {{parent}}.",
    stepChangesNothing: "Ya está en ese lugar: el orden en el estante no cambiaría.",
    showValues: "Listar los valores de {{label}} aquí",
    hideValues: "Ocultar los valores de {{label}}",
    showNextLevel: "Mostrar el nivel bajo {{label}}",
    hideNextLevel: "Ocultar el nivel bajo {{label}}",
    liftLevelHere: "Poner {{label}} sobre {{above}}, solo en esta rama",
    liftLevelShort: "↑",
    liftLevelAbove: "↑ Sobre {{above}}",
    liftLevelTitle: "Poner {{label}} sobre {{above}}, solo en esta rama",
    rungScope: "nivel, dentro de {{scope}}",
    rungDone: "Listo",
    wholePlan: "todo el plan",
    siblingPosition: "{{position}} de {{total}} hermanos",
    collapseNode: "Contraer",
    expandNode: "Expandir",
    selectHint: "Selecciona un nodo para reordenarlo entre sus hermanos.",
    expandBranch: "Expandir {{label}}",
    collapseBranch: "Contraer {{label}}",
    zoomIn: "Acercar",
    zoomOut: "Alejar",
    zoomFit: "Ajustar",
    zoomFitTitle: "Ajustar todo el árbol a la vista",
    zoomLevel: "{{percent}}%",
    expandAllNodes: "Expandir todos los nodos",
    collapseAllNodes: "Contraer todos los nodos",
    expandGraph: "Expandir gráfico",
    collapseGraph: "Contraer gráfico",
    foldTitle: "Cómo se bloquea el mueble:",
    foldThen: ", luego ",
    foldSections_one: "{{label}} (1 sección)",
    foldSections_other: "{{label}} ({{count}} secciones)",
    foldBlocks_one: "{{label}} (1 bloque)",
    foldBlocks_other: "{{label}} ({{count}} bloques)",
    foldExpand: "Expandir",
    foldCollapse: "Contraer",
  },
};
